import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, ListSkeleton } from "@/components/common";
import { fromNow, fmtDateTime } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, ShoppingCart, Check, X, RefreshCw, Trash2, Upload, FileText, Loader2, Building2, CalendarClock, CreditCard } from "lucide-react";

const STATUS = {
  pending_management: { label: "Yönetim Onayı Bekliyor", chip: "bg-amber-100 text-amber-700 border-amber-200" },
  pending_finance: { label: "Finans Onayı Bekliyor", chip: "bg-sky-100 text-sky-700 border-sky-200" },
  revision_proposed: { label: "Revize Edildi – Onay Bekliyor", chip: "bg-violet-100 text-violet-700 border-violet-200" },
  approved: { label: "Onaylandı", chip: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  rejected: { label: "Reddedildi", chip: "bg-rose-100 text-rose-700 border-rose-200" },
  deferred: { label: "İleri Tarihe Ertelendi", chip: "bg-orange-100 text-orange-700 border-orange-200" },
};
const PAY = { cash: "Nakit", transfer: "Havale/EFT", card: "Kredi Kartı", check: "Çek", installment: "Taksit" };
const PAY_OPTS = Object.entries(PAY).map(([id, label]) => ({ id, label }));
const emptyForm = { title: "", description: "", supplier: "", quantity: "", amount: "", currency: "TRY", due_date: "", payment_method: "transfer", payment_terms: "", file: null };

const money = (a, c = "TRY") => `${Number(a || 0).toLocaleString("tr-TR")} ${c === "TRY" ? "₺" : c}`;

async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/upload", fd);
  return data;
}
async function openProforma(pf) {
  try {
    const r = await api.get(`/files/${pf.storage_path}`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch { toast.error("Proforma açılamadı"); }
}

export default function Purchasing() {
  const qc = useQueryClient();
  const { can, user, currentOrg } = useAuth();
  const role = currentOrg?.role;
  const isManagement = ["owner", "admin", "super_admin", "manager"].includes(role);
  const isFinance = can("view_financials");

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);
  const [comment, setComment] = useState("");
  const [revisedTerms, setRevisedTerms] = useState("");
  const [deferDate, setDeferDate] = useState("");
  const [busy, setBusy] = useState(false);

  const { data = [], isLoading } = useQuery({ queryKey: ["purchase-requests"], queryFn: async () => { const d = (await api.get("/purchase-requests")).data; return Array.isArray(d) ? d : []; } });

  const create = async () => {
    if (!form.title.trim()) { toast.error("Başlık gerekli"); return; }
    setSaving(true);
    try {
      let proforma_file_id = null;
      if (form.file) { const up = await uploadFile(form.file); proforma_file_id = up.id; }
      const payload = { ...form, amount: parseFloat(form.amount) || 0, proforma_file_id };
      delete payload.file;
      const { data: p } = await api.post("/purchase-requests", payload);
      toast.success("Satınalma talebi oluşturuldu");
      qc.invalidateQueries({ queryKey: ["purchase-requests"] });
      setCreateOpen(false); setForm(emptyForm); setSelected(p);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const openPr = async (id) => { const { data: p } = await api.get(`/purchase-requests/${id}`); setSelected(p); setComment(""); setRevisedTerms(""); setDeferDate(""); };
  const remove = async (id) => { await api.delete(`/purchase-requests/${id}`); toast.success("Talep silindi"); qc.invalidateQueries({ queryKey: ["purchase-requests"] }); if (selected?.id === id) setSelected(null); };

  const act = async (path, body, msg) => {
    setBusy(true);
    try { await api.post(`/purchase-requests/${selected.id}/${path}`, body); toast.success(msg); await openPr(selected.id); qc.invalidateQueries({ queryKey: ["purchase-requests"] }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const st = (s) => STATUS[s] || STATUS.pending_management;
  const isRequester = selected && selected.created_by === user?.id;

  return (
    <div>
      <PageHeader title="Satınalma Onayları" subtitle="Satınalma taleplerini oluşturun, yönetim ve finans onayından geçirin"
        actions={<Button onClick={() => setCreateOpen(true)} data-testid="new-purchase-button"><Plus className="h-4 w-4 mr-1.5" /> Yeni Talep</Button>} />

      {isLoading ? <ListSkeleton /> : data.length === 0 ? (
        <EmptyState icon={ShoppingCart} title="Henüz talep yok" description="İlk satınalma talebinizi oluşturun; yönetim ve finans onayına düşsün." action={<Button onClick={() => setCreateOpen(true)}><Plus className="h-4 w-4 mr-1.5" /> Yeni Talep</Button>} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {data.map((p) => (
            <Card key={p.id} className="p-4 cursor-pointer hover:shadow-md transition-all group" onClick={() => openPr(p.id)} data-testid={`purchase-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[11px] text-muted-foreground">#{p.number}</p>
                  <h3 className="font-semibold text-sm truncate">{p.title}</h3>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild><button onClick={(e) => e.stopPropagation()} className="md:opacity-0 md:group-hover:opacity-100 text-muted-foreground hover:text-rose-500 transition-opacity shrink-0" data-testid={`purchase-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button></AlertDialogTrigger>
                  <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                    <AlertDialogHeader><AlertDialogTitle>Talebi sil</AlertDialogTitle><AlertDialogDescription>Bu satınalma talebi kalıcı olarak silinsin mi?</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>İptal</AlertDialogCancel><AlertDialogAction onClick={() => remove(p.id)} data-testid="confirm-delete-button">Sil</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
              <div className="flex items-center gap-2 mt-2 flex-wrap text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">{money(p.amount, p.currency)}</span>
                {p.supplier && <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{p.supplier}</span>}
                {p.due_date && <span className="flex items-center gap-1"><CalendarClock className="h-3 w-3" />{p.due_date.slice(0, 10)}</span>}
                <span className="flex items-center gap-1"><CreditCard className="h-3 w-3" />{PAY[p.payment_method]}</span>
              </div>
              <div className="flex items-center justify-between mt-3">
                <Badge variant="outline" className={`text-[10px] ${st(p.status).chip}`}>{st(p.status).label}</Badge>
                {p.proforma && <span className="text-[10px] text-muted-foreground flex items-center gap-1"><FileText className="h-3 w-3" /> Proforma</span>}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="purchase-dialog">
          <DialogHeader><DialogTitle>Yeni Satınalma Talebi</DialogTitle><DialogDescription>Satın alınacak malı, vadesini ve ödeme şeklini belirtin; talep yönetim ve finans onayına gider.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Malzeme / Hizmet</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" data-testid="purchase-title-input" /></div>
            <div><Label>Açıklama</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" rows={2} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tedarikçi</Label><Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} className="mt-1.5" data-testid="purchase-supplier-input" /></div>
              <div><Label>Miktar</Label><Input value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="mt-1.5" placeholder="örn. 10 adet" data-testid="purchase-quantity-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tahmini Tutar (₺)</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1.5" data-testid="purchase-amount-input" /></div>
              <div><Label>Vade / Son Ödeme</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1.5" data-testid="purchase-due-input" /></div>
            </div>
            <div>
              <Label>Ödeme Şekli</Label>
              <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                <SelectTrigger className="mt-1.5" data-testid="purchase-payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>{PAY_OPTS.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Ödeme Koşulu Notu</Label><Textarea value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} className="mt-1.5" rows={2} placeholder="örn. 30 gün vadeli, 2 eşit taksit" data-testid="purchase-terms-input" /></div>
            <div>
              <Label>Proforma (opsiyonel)</Label>
              <label className="mt-1.5 flex items-center justify-center gap-2 border-2 border-dashed rounded-lg p-4 cursor-pointer hover:bg-muted/50 transition-colors" data-testid="purchase-file-drop">
                <Upload className="h-5 w-5 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">{form.file ? form.file.name : "PDF veya görsel seçin"}</span>
                <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} data-testid="purchase-file-input" />
              </label>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCreateOpen(false)}>İptal</Button><Button onClick={create} disabled={saving} data-testid="purchase-save-button">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Gönder</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="purchase-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">#{selected.number} {selected.title}<Badge variant="outline" className={`text-[10px] ${st(selected.status).chip}`} data-testid="purchase-status-badge">{st(selected.status).label}</Badge></DialogTitle>
                <DialogDescription>{selected.created_by_name} • {fromNow(selected.created_at)}</DialogDescription>
              </DialogHeader>

              {selected.description && <p className="text-sm">{selected.description}</p>}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg border p-2.5"><p className="text-[11px] text-muted-foreground">Tutar</p><p className="font-semibold">{money(selected.amount, selected.currency)}</p></div>
                <div className="rounded-lg border p-2.5"><p className="text-[11px] text-muted-foreground">Vade</p><p className="font-medium">{selected.due_date ? selected.due_date.slice(0, 10) : "—"}</p></div>
                <div className="rounded-lg border p-2.5"><p className="text-[11px] text-muted-foreground">Tedarikçi</p><p className="font-medium">{selected.supplier || "—"}</p></div>
                <div className="rounded-lg border p-2.5"><p className="text-[11px] text-muted-foreground">Miktar</p><p className="font-medium">{selected.quantity || "—"}</p></div>
                <div className="rounded-lg border p-2.5"><p className="text-[11px] text-muted-foreground">Ödeme Şekli</p><p className="font-medium">{PAY[selected.payment_method]}</p></div>
                <div className="rounded-lg border p-2.5"><p className="text-[11px] text-muted-foreground">Ödeme Koşulu</p><p className="font-medium">{selected.payment_terms || "—"}</p></div>
              </div>

              {selected.proforma && (
                <Button variant="outline" size="sm" className="w-fit" onClick={() => openProforma(selected.proforma)} data-testid="purchase-proforma-open"><FileText className="h-4 w-4 mr-1.5" /> Proformayı Görüntüle</Button>
              )}

              {selected.revised_terms && (
                <div className="rounded-lg bg-violet-50 border border-violet-200 p-3 text-sm" data-testid="purchase-revised-terms">
                  <p className="font-medium text-violet-800">Finansın önerdiği ödeme şartı</p>
                  <p className="text-violet-700">{selected.revised_terms}</p>
                </div>
              )}

              {selected.scheduled_date && selected.status === "deferred" && (
                <div className="rounded-lg bg-orange-50 border border-orange-200 p-3 text-sm" data-testid="purchase-scheduled-date">
                  <p className="font-medium text-orange-800 flex items-center gap-1.5"><CalendarClock className="h-4 w-4" /> İleri tarihe ertelendi</p>
                  <p className="text-orange-700">Planlanan alım tarihi: {selected.scheduled_date.slice(0, 10)}</p>
                </div>
              )}

              {/* Actions */}
              <div className="space-y-2 border-t pt-3">
                {(selected.status === "pending_management" || selected.status === "deferred") && isManagement && (
                  <div className="space-y-2">
                    <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Karar notu (opsiyonel)" data-testid="purchase-mgmt-comment" />
                    <div className="flex gap-2">
                      <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => act("management-decision", { decision: "approved", comment }, "Yönetim onayı verildi")} data-testid="purchase-mgmt-approve"><Check className="h-4 w-4 mr-1.5" /> Yönetim Onayı</Button>
                      <Button variant="outline" className="text-rose-600" disabled={busy} onClick={() => act("management-decision", { decision: "rejected", comment }, "Talep reddedildi")} data-testid="purchase-mgmt-reject"><X className="h-4 w-4 mr-1.5" /> Reddet</Button>
                    </div>
                    <div className="flex gap-2 items-end pt-1 border-t mt-1">
                      <div className="flex-1">
                        <Label className="text-xs text-muted-foreground">İleri tarihte alım için tarih seçin</Label>
                        <Input type="date" value={deferDate} onChange={(e) => setDeferDate(e.target.value)} className="mt-1" data-testid="purchase-defer-date" />
                      </div>
                      <Button variant="outline" className="text-orange-600 border-orange-300 hover:bg-orange-50" disabled={busy || !deferDate} onClick={() => act("management-decision", { decision: "deferred", scheduled_date: deferDate, comment }, "Talep ileri tarihe ertelendi")} data-testid="purchase-mgmt-defer"><CalendarClock className="h-4 w-4 mr-1.5" /> İleri Tarihe Ertele</Button>
                    </div>
                  </div>
                )}
                {selected.status === "pending_finance" && isFinance && (
                  <div className="space-y-2">
                    <Textarea value={revisedTerms} onChange={(e) => setRevisedTerms(e.target.value)} rows={2} placeholder="Revize ödeme şartı (revize için doldurun)" data-testid="purchase-finance-revised" />
                    <div className="flex gap-2 flex-wrap">
                      <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => act("finance-decision", { decision: "approved", comment }, "Finans onayı verildi")} data-testid="purchase-finance-approve"><Check className="h-4 w-4 mr-1.5" /> Onayla</Button>
                      <Button variant="outline" disabled={busy || !revisedTerms.trim()} onClick={() => act("finance-decision", { decision: "revise", revised_terms: revisedTerms }, "Ödeme şartı revize edildi")} data-testid="purchase-finance-revise"><RefreshCw className="h-4 w-4 mr-1.5" /> Revize Et</Button>
                      <Button variant="outline" className="text-rose-600" disabled={busy} onClick={() => act("finance-decision", { decision: "rejected", comment }, "Talep reddedildi")} data-testid="purchase-finance-reject"><X className="h-4 w-4 mr-1.5" /> Reddet</Button>
                    </div>
                  </div>
                )}
                {selected.status === "revision_proposed" && isRequester && (
                  <div className="flex gap-2">
                    <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => act("respond", { decision: "accepted" }, "Revize şart kabul edildi")} data-testid="purchase-accept-revision"><Check className="h-4 w-4 mr-1.5" /> Revize Şartı Kabul Et</Button>
                    <Button variant="outline" className="text-rose-600" disabled={busy} onClick={() => act("respond", { decision: "declined" }, "Revize şart reddedildi")} data-testid="purchase-decline-revision"><X className="h-4 w-4 mr-1.5" /> Reddet</Button>
                  </div>
                )}
                {(selected.status === "approved" || selected.status === "rejected") && (
                  <p className="text-sm text-muted-foreground">Bu talep sonuçlandı: <span className="font-medium">{st(selected.status).label}</span></p>
                )}
              </div>

              {/* History */}
              <div className="border-t pt-3">
                <p className="text-sm font-medium mb-2">İşlem Geçmişi</p>
                <div className="space-y-2 max-h-44 overflow-y-auto">
                  {(selected.history || []).slice().reverse().map((h, i) => (
                    <div key={i} className="text-xs flex items-start gap-2" data-testid={`purchase-history-${i}`}>
                      <span className="h-1.5 w-1.5 rounded-full bg-indigo-400 mt-1.5 shrink-0" />
                      <div><span className="font-medium">{h.user_name}</span> — {ACTION_LABEL[h.action] || h.action} <span className="text-muted-foreground">· {fmtDateTime(h.at)}</span>{h.note && <p className="text-muted-foreground">{h.note}</p>}</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const ACTION_LABEL = {
  created: "talebi oluşturdu",
  management_approved: "yönetim onayı verdi",
  management_rejected: "yönetim reddetti",
  management_deferred: "ileri tarihe erteledi",
  finance_approved: "finans onayı verdi",
  finance_rejected: "finans reddetti",
  finance_revise: "ödeme şartını revize etti",
  requester_accepted: "revize şartı kabul etti",
  requester_declined: "revize şartı reddetti",
};
